import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Card } from '../UI/Card';
import { Button } from '../UI/Button';
import { signInWithPopup, GoogleAuthProvider } from 'firebase/auth';
import { auth, db, isDBSEmail } from '../../firebase';
import { doc, setDoc, getDoc, serverTimestamp } from 'firebase/firestore';

export const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { signIn } = useAuth();
  const navigate = useNavigate();

  const handleGoogleSignIn = async () => {
    setError('');
    setLoading(true);
    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      const user = result.user;
      if (!isDBSEmail(user.email)) {
        await auth.signOut();
        setError('Please use your DBS school email (@g.dbs.edu.hk)');
        setLoading(false);
        return;
      }
      const userRef = doc(db, 'users', user.uid);
      const snap = await getDoc(userRef);
      if (!snap.exists()) {
        await setDoc(userRef, {
          uid: user.uid, email: user.email,
          displayName: user.displayName || user.email.split('@')[0],
          grade: '', subjectsGoodAt: [], subjectsNeedHelp: [],
          contactWhatsApp: '', contactEmail: '', points: 10,
          rating: 0, ratingCount: 0, isTeacher: false,
          photoURL: user.photoURL, createdAt: serverTimestamp(), lastActive: serverTimestamp()
        });
      } else {
        await setDoc(userRef, { lastActive: serverTimestamp(), photoURL: user.photoURL }, { merge: true });
      }
      navigate('/');
    } catch (err) {
      setError(err.message || 'Google sign-in failed');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await signIn(email, password);
      navigate('/');
    } catch (err) {
      setError(err.message || 'Failed to sign in');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <Card className="auth-card">
        <h2>🎓 Welcome Back</h2>
        <p className="auth-subtitle">Log in to PeerTutor</p>
        {error && <div className="error-message">{error}</div>}
        <button onClick={handleGoogleSignIn} disabled={loading} className="google-btn">
          <svg viewBox="0 0 24 24" width="20" height="20">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          Sign in with Google
        </button>
        <div style={{ textAlign: 'center', margin: '1rem 0', color: '#666', fontSize: '0.875rem' }}>— OR —</div>
        <form onSubmit={handleSubmit}>
          <div className="form-group"><label>School Email</label><input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="School Email" required /></div>
          <div className="form-group"><label>Password</label><input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Enter your password" required minLength={6} /></div>
          <Button type="submit" disabled={loading} className="btn-primary btn-full">{loading ? 'Logging in...' : 'Log In'}</Button>
        </form>
        <p className="auth-footer">Don't have an account? <Link to="/signup">Sign Up</Link></p>
      </Card>
    </div>
  );
};
